import { AssetSetCard } from '@/components/features/assets/AssetSetCard'
import {
  AssetSetSort,
  AssetSetTable,
} from '@/components/features/assets/AssetSetTable'
import { ShareAssetSetDialog } from '@/components/features/assets/ShareAssetSetDialog'
import EditNameModal from '@/components/features/data/EditNameModal'
import ConfirmationDialog from '@/components/utils/ConfirmationDialog'
import PermissionGate from '@/components/utils/PermissionGate'
import { useClient } from '@/context/ClientContext'
import { useAssetSetDownload } from '@/hooks/useAssetSetDownload'
import { useOffsetPaginationQuery } from '@/hooks/useOffsetPaginationQuery'
import { useOrganisationUsersWithUserMap } from '@/hooks/useOrganisationUsersWithUserMap'
import { usePermissionCheck } from '@/hooks/usePermissionCheck'
import { useUserPreferences } from '@/hooks/useUserPreferences'
import { fetchAssetSetsPaginated } from '@/lib/clientHelpers'
import { summaryKeys } from '@/lib/queryKeys'
import { userFor } from '@/lib/userDisplay'
import { cn } from '@/lib/utils'
import type { ViewMode } from '@/types/UserPreferences'
import {
  ArrowsRightLeftIcon,
  Bars3Icon,
  MagnifyingGlassIcon,
  PlusIcon,
  Squares2X2Icon,
} from '@heroicons/react/24/outline'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { z } from 'zod'

import {
  AssetSet,
  AssetSetListSortKey,
  AssetSetWithSummary,
  DownloadJobStatus,
  ListAssetSetsByProjectResult,
} from '@droidmap/asset-service-contract'
import { Button } from '@droidmap/frontend-ui'
import { Input } from '@droidmap/frontend-ui'
import { Pagination } from '@droidmap/frontend-ui'
import { Spinner } from '@droidmap/frontend-ui'
import { ToggleGroup, ToggleGroupItem } from '@droidmap/frontend-ui'
import { Permission } from '@droidmap/permissions-service-contract'
import { AssetSetType } from '@droidmap/shared-service-contract'

const PREFERENCE_KEY = 'PROJECT_ASSET_SETS'

const TYPE_FILTER_VALUES = ['all', 'drone', 'odm'] as const
type TypeFilter = (typeof TYPE_FILTER_VALUES)[number]

const assetSetsSearchSchema = z.object({
  type: z.enum(TYPE_FILTER_VALUES).optional(),
})

export const Route = createFileRoute(
  '/_authenticated/orgs/$orgId_/projects/$projectId/asset-sets',
)({
  validateSearch: assetSetsSearchSchema,
  component: ProjectAssetSetsPage,
  loader: () => ({ crumb: 'Asset Sets' }),
})

// Map the table's sort key (used by AssetSetTable / column headers) to the
// backend list contract's sort columns. Owner column doesn't have a
// natural equivalent on the asset-set table, so it stays out of this map.
const SORT_KEY_TO_BACKEND: Record<AssetSetSort['key'], AssetSetListSortKey> = {
  name: 'name',
  assets: 'assetCount',
  size: 'size',
  created: 'created',
  capturedAt: 'capturedAt',
}

const TYPE_FILTER_TO_BACKEND: Record<TypeFilter, AssetSetType | undefined> = {
  all: undefined,
  drone: AssetSetType.DroneImages,
  odm: AssetSetType.OdmOutputs,
}

export default function ProjectAssetSetsPage() {
  const { orgId, projectId } = Route.useParams()
  const { metadata } = Route.useRouteContext()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { assetClient, organisationClient, userClient } = useClient()
  const [renameErrorId, setRenameErrorId] = useState<string | null>(null)
  const [deleteAssetSetModalOpen, setDeleteAssetSetModalOpen] = useState(false)
  const [assetSetToDelete, setAssetSetToDelete] = useState<AssetSet | null>(
    null,
  )
  const [assetSetToEdit, setAssetSetToEdit] = useState<AssetSet | null>(null)
  const [assetSetToShare, setAssetSetToShare] = useState<AssetSet | null>(null)
  const [downloadingAssetSetId, setDownloadingAssetSetId] = useState<
    string | null
  >(null)
  const [filter, setFilter] = useState('')
  // Type filter is URL-driven so deep links from other panels (e.g.
  // Outputs → ?type=odm) land on the page with the right pill selected.
  const search = Route.useSearch()
  const typeFilter: TypeFilter = search.type ?? 'all'
  const setTypeFilter = useCallback(
    (next: TypeFilter) => {
      void navigate({
        to: '/orgs/$orgId/projects/$projectId/asset-sets',
        params: { orgId, projectId },
        search: { type: next === 'all' ? undefined : next },
        replace: true,
      })
    },
    [navigate, orgId, projectId],
  )
  const [sort, setSort] = useState<AssetSetSort>({
    key: 'created',
    dir: 'desc',
  })
  const { getTableViewMode, setTableViewMode } = useUserPreferences()
  const [viewMode, setViewMode] = useState<ViewMode>(
    () => getTableViewMode(PREFERENCE_KEY) ?? 'grid',
  )
  const handleViewModeChange = useCallback(
    (next: ViewMode) => {
      setViewMode(next)
      setTableViewMode(PREFERENCE_KEY, next)
    },
    [setTableViewMode],
  )

  const trimmedFilter = filter.trim()
  const assetSetsQueryKey = useMemo(
    () => ['assetSets', projectId] as const,
    [projectId],
  )

  const { canCreate, canView, hasPermission } = usePermissionCheck()
  const canRenameAssetSet = hasPermission(Permission.UPDATE_ASSET_SET)
  // Share links expose the set publicly — gated on the same write permission
  // the backend enforces for share-link creation.
  const canShareAssetSet = canRenameAssetSet

  const {
    initiateDownload,
    downloadJob,
    failedAssets: downloadFailedAssets,
    error: downloadError,
    reset: resetDownload,
  } = useAssetSetDownload(metadata)

  // A failed job clears the in-progress marker immediately — a pure reaction to
  // the job status, so it is decided during render. In the effect below it was a
  // synchronous setState (the cascading render the compiler flags) and left the
  // failed set rendering as "still downloading" for one commit.
  if (
    downloadJob?.status === DownloadJobStatus.Failed &&
    downloadingAssetSetId
  ) {
    setDownloadingAssetSetId(null)
  }

  // The success case is genuinely time-based: hold the completed state on screen
  // briefly, then clear it.
  useEffect(() => {
    if (downloadJob?.status !== DownloadJobStatus.Completed) {
      return undefined
    }
    if (downloadFailedAssets > 0) {
      return undefined
    }
    const timer = setTimeout(() => {
      setDownloadingAssetSetId(null)
      resetDownload()
    }, 2000)
    return () => clearTimeout(timer)
  }, [downloadJob?.status, downloadFailedAssets, downloadError, resetDownload])

  const { userMap } = useOrganisationUsersWithUserMap({
    orgId,
    organisationClient,
    userClient,
    metadata,
  })

  // Only the grid view renders thumbnails, so only it pays for the extra
  // per-set presign. Threaded through resetKey so toggling views refetches
  // rather than serving the summary-less table page from cache.
  const includeSummaries = viewMode === 'grid'

  const {
    data: assetSets,
    rawData,
    isLoading: isLoadingAssetSets,
    pagination,
    refresh: refreshPagination,
  } = useOffsetPaginationQuery<
    AssetSetWithSummary,
    ListAssetSetsByProjectResult
  >({
    preferenceKey: PREFERENCE_KEY,
    queryKey: assetSetsQueryKey,
    queryFn: ({ page, pageSize }) =>
      fetchAssetSetsPaginated(projectId, assetClient, metadata, {
        page,
        pageSize,
        filter: trimmedFilter || undefined,
        sortBy: SORT_KEY_TO_BACKEND[sort.key],
        sortDir: sort.dir,
        assetSetType: TYPE_FILTER_TO_BACKEND[typeFilter],
        includeSummaries,
      }),
    resetKey: {
      filter: trimmedFilter,
      sortKey: sort.key,
      sortDir: sort.dir,
      typeFilter,
      includeSummaries,
    },
  })

  const typeCounts = rawData?.typeCounts ?? { drone: 0, odm: 0 }
  const counts = {
    all: typeCounts.drone + typeCounts.odm,
    drone: typeCounts.drone,
    odm: typeCounts.odm,
  }

  const renameMutation = useMutation({
    mutationFn: async (input: { assetSetId: string; name: string }) =>
      assetClient.updateAssetSet(input, metadata),
    onMutate: () => {
      setRenameErrorId(null)
    },
    onSuccess: async () => {
      setAssetSetToEdit(null)
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: assetSetsQueryKey,
        }),
        queryClient.invalidateQueries({
          queryKey: summaryKeys.assetSets(projectId),
        }),
      ])
    },
    onError: (_err, { assetSetId }) => {
      setRenameErrorId(assetSetId)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (assetSetId: string) => {
      return await assetClient.deleteAssetSet({ assetSetId }, metadata)
    },
    onSuccess: async () => {
      refreshPagination()
      await queryClient.invalidateQueries({
        queryKey: summaryKeys.assetSets(projectId),
      })
      setDeleteAssetSetModalOpen(false)
      setAssetSetToDelete(null)
    },
  })

  const handleCreateAssetSet = () => {
    navigate({ to: `/orgs/${orgId}/projects/${projectId}/asset-sets/create` })
  }

  const handleCompareAssets = () => {
    navigate({ to: `/orgs/${orgId}/projects/${projectId}/compare` })
  }

  const handleViewAssetSet = useCallback(
    (assetSet: AssetSet) => {
      navigate({
        to: `/orgs/${orgId}/projects/${projectId}/asset-sets/${assetSet.id}`,
      })
    },
    [navigate, orgId, projectId],
  )

  const handleDownloadAssetSet = useCallback(
    (assetSet: AssetSet) => {
      setDownloadingAssetSetId(assetSet.id)
      initiateDownload(assetSet.id)
    },
    [initiateDownload],
  )

  const handleDeleteAssetSet = (assetSet: AssetSet) => {
    setAssetSetToDelete(assetSet)
    setDeleteAssetSetModalOpen(true)
  }

  const confirmDeleteAssetSet = () => {
    if (assetSetToDelete) {
      deleteMutation.mutate(assetSetToDelete.id)
    }
  }

  return (
    <>
      {/* Toolbar */}
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3.5">
        <div className="flex flex-wrap items-center gap-2.5">
          <div className="w-72 max-w-full">
            <Input
              id="asset-set-filter"
              name="filter"
              type="text"
              placeholder="Filter asset sets…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              leadingIcon={<MagnifyingGlassIcon className="h-3.5 w-3.5" />}
              className="h-10"
            />
          </div>
          <div
            role="group"
            aria-label="Type filter"
            className="border-border-strong bg-bg-2 inline-flex h-10 items-center gap-0.5 rounded-md border p-[3px]"
          >
            {(
              [
                { key: 'all' as TypeFilter, label: 'All', count: counts.all },
                {
                  key: 'drone' as TypeFilter,
                  label: 'Drone',
                  count: counts.drone,
                },
                {
                  key: 'odm' as TypeFilter,
                  label: 'ODM',
                  count: counts.odm,
                },
              ] as const
            ).map(({ key, label, count }) => (
              <button
                key={key}
                type="button"
                onClick={() => setTypeFilter(key)}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-sm px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.06em] transition-colors duration-150',
                  typeFilter === key
                    ? 'bg-panel text-accent shadow-xs'
                    : 'text-text-dim hover:text-text',
                )}
              >
                {label}
                <span
                  className={cn(
                    'border-border bg-bg-2 text-text-mute min-w-[16px] rounded-full border px-1.5 text-center font-mono text-[9px]',
                    typeFilter === key &&
                      'bg-accent-soft text-accent border-accent-tint',
                  )}
                >
                  {count}
                </span>
              </button>
            ))}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2.5">
          {downloadingAssetSetId && (
            <span className="text-accent border-accent-tint bg-accent-soft inline-flex items-center rounded-md border px-2.5 py-1 font-mono text-[11px] uppercase tracking-[0.06em]">
              Downloading…
            </span>
          )}
          {canView && (
            <Button variant="ghost" onClick={handleCompareAssets}>
              <ArrowsRightLeftIcon className="h-4 w-4" />
              Compare
            </Button>
          )}
          <ToggleGroup
            type="single"
            size="sm"
            className="h-10 p-[3px]"
            value={viewMode}
            onValueChange={(value) => {
              if (value === 'grid' || value === 'table') {
                handleViewModeChange(value)
              }
            }}
            aria-label="View mode"
          >
            <ToggleGroupItem value="grid" aria-label="Grid view" title="Grid">
              <Squares2X2Icon className="h-3.5 w-3.5" />
            </ToggleGroupItem>
            <ToggleGroupItem
              value="table"
              aria-label="Table view"
              title="Table"
            >
              <Bars3Icon className="h-3.5 w-3.5" />
            </ToggleGroupItem>
          </ToggleGroup>
          {canCreate && (
            <Button onClick={handleCreateAssetSet}>
              <PlusIcon className="h-4 w-4" />
              New Asset Set
            </Button>
          )}
        </div>
      </div>

      {isLoadingAssetSets && assetSets.length === 0 ? (
        <div className="flex justify-center py-16">
          <Spinner size="page" />
        </div>
      ) : assetSets.length === 0 ? (
        <div className="border-border bg-panel rounded-lg border p-12 text-center">
          <p className="text-text-dim">
            {trimmedFilter || typeFilter !== 'all'
              ? 'No asset sets match the current filter.'
              : 'No asset sets in this project yet.'}
          </p>
        </div>
      ) : viewMode === 'table' ? (
        <AssetSetTable
          assetSets={assetSets}
          userMap={userMap}
          onView={handleViewAssetSet}
          onDownload={handleDownloadAssetSet}
          onDelete={handleDeleteAssetSet}
          onEdit={canRenameAssetSet ? setAssetSetToEdit : undefined}
          onShare={canShareAssetSet ? setAssetSetToShare : undefined}
          sort={sort}
          onSortChange={setSort}
          pagination={pagination}
        />
      ) : (
        <>
          <div className="grid grid-cols-[repeat(auto-fill,minmax(300px,1fr))] gap-4">
            {assetSets.map((assetSet) => (
              <AssetSetCard
                key={assetSet.id}
                assetSet={assetSet}
                summary={assetSet.summary}
                creator={userFor(userMap, assetSet.createdBy)}
                orgId={orgId}
                projectId={projectId}
                onDownload={handleDownloadAssetSet}
                onDelete={handleDeleteAssetSet}
                onEdit={canRenameAssetSet ? setAssetSetToEdit : undefined}
                onShare={canShareAssetSet ? setAssetSetToShare : undefined}
              />
            ))}
          </div>
          {(pagination.pageCount ?? 1) > 1 && (
            <div className="mt-6 flex justify-center">
              <Pagination
                currentPage={pagination.state.pageIndex + 1}
                maxPage={pagination.pageCount ?? 1}
                hasMoreBeyondKnownPages={
                  pagination.hasMoreBeyondKnownPages ?? false
                }
                onPageChange={(page) =>
                  pagination.onChange({
                    ...pagination.state,
                    pageIndex: page - 1,
                  })
                }
              />
            </div>
          )}
        </>
      )}

      {assetSetToShare && (
        <ShareAssetSetDialog
          assetSet={assetSetToShare}
          isOpen
          onClose={() => setAssetSetToShare(null)}
          metadata={metadata}
        />
      )}

      <EditNameModal
        isOpen={assetSetToEdit !== null}
        onClose={() => {
          setAssetSetToEdit(null)
          setRenameErrorId(null)
        }}
        entityType="Asset Set"
        initialName={assetSetToEdit?.name ?? ''}
        isPending={renameMutation.isPending}
        error={
          renameMutation.isError && renameErrorId === assetSetToEdit?.id
            ? renameMutation.error
            : null
        }
        onSubmit={(newName) => {
          if (!assetSetToEdit) return
          renameMutation.mutate({
            assetSetId: assetSetToEdit.id,
            name: newName,
          })
        }}
      />

      <PermissionGate permission={Permission.DELETE_ASSET_SET}>
        <ConfirmationDialog
          title="Delete Asset Set"
          description={
            <>
              You&rsquo;re about to permanently delete{' '}
              <strong className="font-semibold text-text">
                {assetSetToDelete?.name ?? 'this asset set'}
              </strong>
              , including all of its assets. This can&rsquo;t be undone.
            </>
          }
          isOpen={deleteAssetSetModalOpen}
          onClose={() => {
            setDeleteAssetSetModalOpen(false)
            setAssetSetToDelete(null)
          }}
          onConfirm={confirmDeleteAssetSet}
          confirmText={deleteMutation.isPending ? 'Deleting...' : 'Delete'}
          cancelText="Cancel"
          variant="destructive"
        />
      </PermissionGate>
    </>
  )
}
